import React, { useState, useEffect } from 'react';
import useWindowWidth from '../hooks/useWindowWidth';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBuilding, 
  faUsers, 
  faChartBar,
  faExclamationTriangle,
  faClock,
  faSignInAlt,
  faSignOutAlt,
  faCheckCircle,
  faTasks,
  faShieldAlt,
  faCalendarAlt,
  faUserPlus,
  faArrowLeft,
  faTimes,
  faSpinner,
  faSitemap,
  faPlus,
  faEdit,
  faTrash,
  faUserTie,
  faUserMinus,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import { companyAPI } from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Dashboard.css';

const CompanyDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [employees, setEmployees] = useState([]);       // full data (admin only)
  const [allEmployeesList, setAllEmployeesList] = useState([]); // lightweight list (admin + manager)
  const [heatmap, setHeatmap] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Role state
  const [userRole, setUserRole] = useState(null); // 'admin' | 'manager' | null
  const [managedDeptIds, setManagedDeptIds] = useState([]);

  // Selected department for managing members/tasks
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptView, setDeptView] = useState('list'); // list | members | tasks

  // Department task view
  const [deptTasks, setDeptTasks] = useState([]);
  const [loadingDeptTasks, setLoadingDeptTasks] = useState(false);

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('employee');
  const [inviting, setInviting] = useState(false);

  // Department modal
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [deptForm, setDeptForm] = useState({ name: '', description: '', manager_id: '' });
  const [savingDept, setSavingDept] = useState(false);

  // Add member modal
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedMemberUserId, setSelectedMemberUserId] = useState('');
  const [addingMember, setAddingMember] = useState(false);

  // Assign task modal
  const [showAssignTaskModal, setShowAssignTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    weight: 'medium',
    assignee_id: '',
    due_date: new Date().toISOString().split('T')[0]
  });
  const [assigningTask, setAssigningTask] = useState(false);

  // Task list filters & pagination
  const [taskSearch, setTaskSearch] = useState('');
  const [taskFilterStatus, setTaskFilterStatus] = useState('all');
  const [taskFilterWeight, setTaskFilterWeight] = useState('all');
  const [taskFilterDateFrom, setTaskFilterDateFrom] = useState('');
  const [taskFilterDateTo, setTaskFilterDateTo] = useState('');
  const [taskFilterAssignee, setTaskFilterAssignee] = useState('');
  const [taskPage, setTaskPage] = useState(1);
  const TASKS_PER_PAGE = 8;

  // Task sub-view: 'list' | 'report'
  const [taskSubView, setTaskSubView] = useState('list');
  const [deptReport, setDeptReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportDateFrom, setReportDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [reportDateTo, setReportDateTo] = useState(new Date().toISOString().split('T')[0]);
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 768;
  const isSmall = windowWidth <= 480;

  useEffect(() => {
    checkAccess();
    fetchCompanyData();
  }, []);

  const checkAccess = async () => {
    try {
      const response = await companyAPI.checkAccess();
      const data = response.data;
      if (!data.has_access) {
        navigate('/dashboard');
        toast.error('Access denied. Only company administrators or managers can access this page.');
        return;
      }
      setUserRole(data.role); // 'admin' or 'manager'
      setManagedDeptIds(data.managed_department_ids || []);
      // Managers start on departments tab
      if (data.role === 'manager') {
        setActiveTab('departments');
      }
    } catch (error) {
      console.error('Access check failed:', error);
      navigate('/dashboard');
    }
  };

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      const dashboardRes = await companyAPI.getCompanyStats();
      setDashboardData(dashboardRes.data);

      const role = dashboardRes.data.is_admin ? 'admin' : 'manager';

      // Always load the lightweight employee list (needed for Add Member dropdown)
      const listRes = await companyAPI.listEmployees();
      setAllEmployeesList(listRes.data.employees || []);

      if (role === 'admin') {
        const [employeesRes, heatmapRes] = await Promise.all([
          companyAPI.getEmployees(),
          companyAPI.getWorkloadHeatmap()
        ]);
        setEmployees(employeesRes.data.employees || []);
        setHeatmap(heatmapRes.data.heatmap || []);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        navigate('/dashboard');
        toast.error('Access denied.');
      } else {
        toast.error('Failed to load company data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await companyAPI.getDepartments();
      setDepartments(res.data.departments || []);
    } catch (error) {
      toast.error('Failed to load departments');
    }
  };

  useEffect(() => {
    if (activeTab === 'departments') {
      fetchDepartments();
    }
  }, [activeTab]);

  const isAdmin = userRole === 'admin';

  // ---- Dept CRUD ----
  const openCreateDept = () => {
    setEditingDept(null);
    setDeptForm({ name: '', description: '', manager_id: '' });
    setShowDeptModal(true);
  };

  const openEditDept = (dept) => {
    setEditingDept(dept);
    setDeptForm({ name: dept.name, description: dept.description || '', manager_id: dept.manager_id || '' });
    setShowDeptModal(true);
  };

  const saveDepartment = async (e) => {
    e.preventDefault();
    if (!deptForm.name.trim()) { toast.error('Department name is required'); return; }
    setSavingDept(true);
    try {
      const payload = {
        name: deptForm.name,
        description: deptForm.description,
        manager_id: deptForm.manager_id ? parseInt(deptForm.manager_id) : null
      };
      if (editingDept) {
        await companyAPI.updateDepartment(editingDept.id, payload);
        toast.success('Department updated!');
      } else {
        await companyAPI.createDepartment(payload);
        toast.success('Department created!');
      }
      setShowDeptModal(false);
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save department');
    } finally {
      setSavingDept(false);
    }
  };

  const deleteDepartment = async (dept) => {
    if (!window.confirm(`Delete department "${dept.name}"? This will not delete tasks.`)) return;
    try {
      await companyAPI.deleteDepartment(dept.id);
      toast.success('Department deleted');
      if (selectedDept?.id === dept.id) { setSelectedDept(null); setDeptView('list'); }
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete department');
    }
  };

  // ---- Members ----
  const openManageMembers = (dept) => {
    setSelectedDept(dept);
    setDeptView('members');
  };

  const addMember = async (e) => {
    e.preventDefault();
    if (!selectedMemberUserId) { toast.error('Select an employee'); return; }
    setAddingMember(true);
    try {
      await companyAPI.addDepartmentMember(selectedDept.id, { user_id: parseInt(selectedMemberUserId) });
      toast.success('Employee added to department!');
      setShowAddMemberModal(false);
      setSelectedMemberUserId('');
      fetchDepartments(); // refresh to get updated members
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  const removeMember = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from this department?`)) return;
    try {
      await companyAPI.removeDepartmentMember(selectedDept.id, userId);
      toast.success('Member removed');
      fetchDepartments();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  // ---- Tasks ----
  const openManageTasks = async (dept) => {
    setSelectedDept(dept);
    setDeptView('tasks');
    setTaskSubView('list');
    setTaskPage(1);
    setTaskSearch('');
    setTaskFilterStatus('all');
    setTaskFilterWeight('all');
    setTaskFilterDateFrom('');
    setTaskFilterDateTo('');
    setTaskFilterAssignee('');
    setDeptReport(null);
    setLoadingDeptTasks(true);
    try {
      const res = await companyAPI.getDepartmentTasks(dept.id);
      setDeptTasks(res.data.tasks || []);
    } catch (error) {
      toast.error('Failed to load department tasks');
    } finally {
      setLoadingDeptTasks(false);
    }
  };

  const fetchDeptReport = async (dept) => {
    setLoadingReport(true);
    try {
      const res = await companyAPI.getDepartmentReport(dept.id, {
        date_from: reportDateFrom,
        date_to: reportDateTo
      });
      setDeptReport(res.data);
    } catch (error) {
      // Build a client-side report from existing deptTasks if API not available
      const completed = deptTasks.filter(t => t.status === 'completed');
      const late = deptTasks.filter(t => {
        if (!t.scheduled_date) return false;
        return new Date(t.scheduled_date) < new Date() && t.status !== 'completed';
      });
      const byAssignee = {};
      deptTasks.forEach(t => {
        const name = t.assignee_name || 'Unknown';
        if (!byAssignee[name]) byAssignee[name] = { total: 0, completed: 0, late: 0 };
        byAssignee[name].total++;
        if (t.status === 'completed') byAssignee[name].completed++;
        if (new Date(t.scheduled_date) < new Date() && t.status !== 'completed') byAssignee[name].late++;
      });
      setDeptReport({
        total: deptTasks.length,
        completed: completed.length,
        late: late.length,
        completion_rate: deptTasks.length ? Math.round((completed.length / deptTasks.length) * 100) : 0,
        by_assignee: Object.entries(byAssignee).map(([name, stats]) => ({ name, ...stats }))
      });
    } finally {
      setLoadingReport(false);
    }
  };

  const assignTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) { toast.error('Task title is required'); return; }
    if (!taskForm.assignee_id) { toast.error('Select an employee to assign to'); return; }
    setAssigningTask(true);
    try {
      await companyAPI.assignDepartmentTask(selectedDept.id, {
        ...taskForm,
        assignee_id: parseInt(taskForm.assignee_id)
      });
      toast.success('Task assigned successfully!');
      setShowAssignTaskModal(false);
      setTaskForm({ title: '', description: '', weight: 'medium', assignee_id: '', due_date: new Date().toISOString().split('T')[0] });
      // Reload tasks
      const res = await companyAPI.getDepartmentTasks(selectedDept.id);
      setDeptTasks(res.data.tasks || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to assign task');
    } finally {
      setAssigningTask(false);
    }
  };

  // ---- Employee actions ----
  const handleInviteEmployee = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) { toast.error('Please enter an email address'); return; }
    setInviting(true);
    try {
      const response = await companyAPI.inviteEmployee({ email: inviteEmail, role: inviteRole });
      toast.success(response.data.message || 'Employee invited successfully!');
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('employee');
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to invite employee');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to remove ${employeeName} from the company?`)) return;
    try {
      await companyAPI.removeEmployee(employeeId);
      toast.success('Employee removed successfully');
      fetchCompanyData();
    } catch (error) {
      toast.error('Failed to remove employee');
    }
  };

  // ---- Helpers ----
  const getLoadColor = (loadScore) => {
    if (loadScore <= 3) return '#10b981';
    if (loadScore <= 6) return '#f59e0b';
    return '#ef4444';
  };

  const getRiskBadgeClass = (riskLevel) => {
    const classes = { 'low': 'status-badge success', 'moderate': 'status-badge warning', 'high': 'status-badge danger', 'unknown': 'status-badge' };
    return classes[riskLevel] || 'status-badge';
  };

  const formatClockTime = (timeString) => {
    if (!timeString) return '-';
    return new Date(timeString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Managers among employees
  const managers = employees.filter(e => e.role === 'manager');

  // Members of selectedDept
  const currentDeptData = departments.find(d => d.id === selectedDept?.id);
  const deptMembers = currentDeptData?.members || [];

  // Employees not yet in selectedDept — use allEmployeesList so managers can also see the list
  const availableToAdd = selectedDept
    ? allEmployeesList.filter(e => !deptMembers.some(m => m.user_id === e.user_id))
    : [];

  // Employees available to assign tasks in this dept
  const deptMembersForTask = deptMembers;

  const btnStyle = {
    backgroundColor: '#FFB6C1',
    color: '#000',
    border: 'none',
    padding: '0.6rem 1.2rem',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s',
    fontSize: '0.9rem'
  };

  const dangerBtnStyle = {
    ...btnStyle,
    backgroundColor: '#ef4444',
    color: '#fff',
    padding: '0.4rem 0.8rem',
    fontSize: '0.8rem'
  };

  const ghostBtnStyle = {
    background: 'none',
    border: '1px solid #FFB6C1',
    color: '#FFB6C1',
    padding: '0.4rem 0.8rem',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontSize: '0.85rem',
    transition: 'all 0.2s'
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-center">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#FFB6C1' }} />
          <p style={{ marginTop: '1rem', color: '#999' }}>Loading company dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container" style={{ padding: isMobile ? '1rem' : undefined }}>
      <ToastContainer position="top-right" autoClose={3000} />
      
      <button onClick={() => navigate('/dashboard')} style={{ ...ghostBtnStyle, marginBottom: '1.5rem' }}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
      </button>
      
      <div className="dashboard-header" style={isMobile ? { flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' } : {}}>
        <div>
          <h1 style={isMobile ? { fontSize: '1.4rem' } : {}}><FontAwesomeIcon icon={faBuilding} /> Company Dashboard</h1>
          <p style={isMobile ? { fontSize: '0.9rem' } : {}}>Task-Based Workload Intelligence for Teams</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tabs-container" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', flexWrap: 'nowrap' }}>
        {/* Admin-only tabs */}
        {isAdmin && ['overview', 'employees', 'heatmap'].map(tab => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            <FontAwesomeIcon icon={tab === 'overview' ? faChartBar : tab === 'employees' ? faUsers : faCalendarAlt} />
            {' '}{tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
        {/* Departments tab — visible to both admin and manager */}
        <button
          className={`tab-btn ${activeTab === 'departments' ? 'active' : ''}`}
          onClick={() => { setActiveTab('departments'); setDeptView('list'); setSelectedDept(null); }}
          style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          <FontAwesomeIcon icon={faSitemap} /> Departments
        </button>
      </div>

      {/* ===== OVERVIEW TAB ===== */}
      {activeTab === 'overview' && (
        <div className="dashboard-grid">
          {[
            { label: 'Total Employees', value: dashboardData?.total_employees || 0, icon: faUsers },
            { label: 'Clocked In Today', value: dashboardData?.today_clock_ins || 0, icon: faSignInAlt, color: '#10b981' },
            { label: 'Clocked Out Today', value: dashboardData?.today_clock_outs || 0, icon: faSignOutAlt },
            { label: 'Overloaded Employees', value: dashboardData?.overloaded_employees || 0, icon: faExclamationTriangle, color: '#ef4444' },
            { label: 'High Burnout Risk', value: dashboardData?.high_risk_burnout || 0, icon: faShieldAlt, color: '#dc2626' },
          ].map(({ label, value, icon, color }) => (
            <div key={label} className="dashboard-card" onClick={() => setActiveTab('employees')} style={{ cursor: 'pointer' }}>
              <h3><FontAwesomeIcon icon={icon} /> {label}</h3>
              <div className="stat-value" style={color ? { color } : {}}>{value}</div>
            </div>
          ))}

          <div className="dashboard-card full-width">
            <h2><FontAwesomeIcon icon={faChartBar} /> Task Overload Detection</h2>
            <div className="insights-list">
              {dashboardData?.overloaded_employees > 0 ? (
                <div className="insight-item warning">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <p><strong>Consistent task overload detected.</strong><br />
                    {dashboardData.overloaded_employees} employee(s) have excessive task volume or pace.</p>
                </div>
              ) : (
                <div className="insight-item success">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <p><strong>Workload is balanced.</strong><br />No employees are showing signs of task overload.</p>
                </div>
              )}
              {dashboardData?.high_risk_burnout > 0 && (
                <div className="insight-item danger">
                  <FontAwesomeIcon icon={faShieldAlt} />
                  <p><strong>Burnout risk elevated.</strong><br />
                    {dashboardData.high_risk_burnout} employee(s) showing high burnout risk. Immediate attention recommended.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== EMPLOYEES TAB ===== */}
      {activeTab === 'employees' && (
        <div className="dashboard-grid">
          <div className="dashboard-card full-width">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <h2 style={isMobile ? { fontSize: '1.2rem' } : {}}><FontAwesomeIcon icon={faUsers} /> Employee Status</h2>
              <button style={btnStyle} onClick={() => setShowInviteModal(true)}>
                <FontAwesomeIcon icon={faUserPlus} /> Invite Employee
              </button>
            </div>
            <div className="employees-table-container">
              <table className="employees-table">
                <thead>
                  <tr>
                    <th>Employee</th><th>Email</th><th>Role</th><th>Status</th>
                    <th>Clock In</th><th>Clock Out</th><th>Active Tasks</th><th>Burnout Risk</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((employee) => (
                    <tr key={employee.employee_id}>
                      <td>{employee.full_name || 'N/A'}</td>
                      <td>{employee.email}</td>
                      <td className="capitalize">{employee.role}</td>
                      <td>
                        {employee.clocked_in && !employee.clocked_out
                          ? <span className="status-badge success"><FontAwesomeIcon icon={faClock} /> Active</span>
                          : employee.clocked_out
                          ? <span className="status-badge"><FontAwesomeIcon icon={faSignOutAlt} /> Clocked Out</span>
                          : <span className="status-badge">Not Started</span>}
                      </td>
                      <td>{employee.clock_in_time ? <span style={{ color: '#10b981' }}><FontAwesomeIcon icon={faSignInAlt} /> {formatClockTime(employee.clock_in_time)}</span> : <span style={{ color: '#666' }}>-</span>}</td>
                      <td>{employee.clock_out_time ? <span style={{ color: '#FFB6C1' }}><FontAwesomeIcon icon={faSignOutAlt} /> {formatClockTime(employee.clock_out_time)}</span> : <span style={{ color: '#666' }}>-</span>}</td>
                      <td><span style={{ color: employee.active_tasks >= 5 ? '#ef4444' : '#fff' }}><FontAwesomeIcon icon={faTasks} /> {employee.active_tasks}</span></td>
                      <td><span className={getRiskBadgeClass(employee.burnout_risk)}>{employee.burnout_risk || 'Unknown'}</span></td>
                      <td>
                        <button style={dangerBtnStyle} onClick={() => handleRemoveEmployee(employee.employee_id, employee.full_name)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                  {employees.length === 0 && (
                    <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>No employees yet. Click "Invite Employee" to get started.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ===== DEPARTMENTS TAB ===== */}
      {activeTab === 'departments' && (
        <div className="dashboard-grid">
          {/* Breadcrumb */}
          {deptView !== 'list' && selectedDept && (
            <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <button style={ghostBtnStyle} onClick={() => { setDeptView('list'); setSelectedDept(null); }}>
                <FontAwesomeIcon icon={faArrowLeft} /> All Departments
              </button>
              <span style={{ color: '#999' }}>›</span>
              <span style={{ color: '#FFB6C1', fontWeight: '600' }}>{selectedDept.name}</span>
              <span style={{ color: '#999' }}>›</span>
              <span style={{ color: '#ccc' }}>{deptView === 'members' ? 'Members' : 'Tasks'}</span>
            </div>
          )}

          {/* ---- LIST VIEW ---- */}
          {deptView === 'list' && (
            <div className="dashboard-card full-width">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2><FontAwesomeIcon icon={faSitemap} /> {isAdmin ? 'Departments' : 'My Departments'}</h2>
                {isAdmin && (
                  <button style={btnStyle} onClick={openCreateDept}>
                    <FontAwesomeIcon icon={faPlus} /> New Department
                  </button>
                )}
              </div>
              {departments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
                  <FontAwesomeIcon icon={faSitemap} size="3x" style={{ opacity: 0.3, marginBottom: '1rem' }} />
                  <p>No departments yet.</p>
                  <p style={{ fontSize: '0.9rem' }}>Create a department to start organizing your team.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                  {departments.map(dept => (
                    <div key={dept.id} style={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333',
                      borderRadius: '12px',
                      padding: '1.5rem',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <h3 style={{ margin: 0, color: '#FFB6C1', fontSize: '1.1rem' }}>{dept.name}</h3>
                        {isAdmin && (
                          <div style={{ display: 'flex', gap: '0.4rem' }}>
                            <button style={ghostBtnStyle} title="Edit" onClick={() => openEditDept(dept)}>
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button style={{ ...ghostBtnStyle, borderColor: '#ef4444', color: '#ef4444' }} title="Delete" onClick={() => deleteDepartment(dept)}>
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        )}
                      </div>
                      {dept.description && <p style={{ color: '#999', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{dept.description}</p>}
                      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                        <span style={{ color: '#aaa', fontSize: '0.85rem' }}>
                          <FontAwesomeIcon icon={faUserTie} style={{ marginRight: '0.4rem', color: '#FFB6C1' }} />
                          {dept.manager_name || <em style={{ color: '#555' }}>No manager</em>}
                        </span>
                        <span style={{ color: '#aaa', fontSize: '0.85rem' }}>
                          <FontAwesomeIcon icon={faUsers} style={{ marginRight: '0.4rem', color: '#FFB6C1' }} />
                          {dept.member_count} member{dept.member_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                        <button style={{ ...ghostBtnStyle, justifyContent: 'center' }} onClick={() => openManageMembers(dept)}>
                          <FontAwesomeIcon icon={faUsers} /> Members
                        </button>
                        <button style={{ ...ghostBtnStyle, justifyContent: 'center' }} onClick={() => openManageTasks(dept)}>
                          <FontAwesomeIcon icon={faClipboardList} /> Tasks
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ---- MEMBERS VIEW ---- */}
          {deptView === 'members' && selectedDept && (
            <div className="dashboard-card full-width">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2><FontAwesomeIcon icon={faUsers} /> {selectedDept.name} — Members</h2>
                <button style={btnStyle} onClick={() => setShowAddMemberModal(true)}>
                  <FontAwesomeIcon icon={faUserPlus} /> Add Member
                </button>
              </div>
              {deptMembers.length === 0 ? (
                <p style={{ color: '#999', textAlign: 'center', padding: '2rem' }}>No members yet. Add employees to this department.</p>
              ) : (
                <table className="employees-table">
                  <thead>
                    <tr><th>Name</th><th>Email</th><th>Added</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {deptMembers.map(member => (
                      <tr key={member.id}>
                        <td>{member.user_name}</td>
                        <td>{member.user_email}</td>
                        <td>{member.added_at ? new Date(member.added_at).toLocaleDateString() : '-'}</td>
                        <td>
                          <button style={dangerBtnStyle} onClick={() => removeMember(member.user_id, member.user_name)}>
                            <FontAwesomeIcon icon={faUserMinus} /> Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ---- TASKS VIEW ---- */}
          {deptView === 'tasks' && selectedDept && (() => {
            const today = new Date();
            today.setHours(0,0,0,0);

            const getTaskStatus = (task) => {
              if (task.status === 'completed') return 'completed';
              const due = task.scheduled_date ? new Date(task.scheduled_date) : null;
              if (due && due < today) return 'late';
              return 'ongoing';
            };

            const statusBadge = (task) => {
              const s = getTaskStatus(task);
              const cfg = {
                completed: { bg: '#4ade8020', color: '#4ade80', border: '#4ade8040', label: 'Completed' },
                late:      { bg: '#ef444420', color: '#ef4444', border: '#ef444440', label: '🔴 Late' },
                ongoing:   { bg: '#f59e0b20', color: '#f59e0b', border: '#f59e0b40', label: '🟡 Ongoing' },
              }[s];
              return (
                <span style={{ padding: '0.25rem 0.65rem', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '700',
                  backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, whiteSpace: 'nowrap' }}>
                  {cfg.label}
                </span>
              );
            };

            const weightBadge = (w) => {
              const wCfg = { heavy: { bg:'#FF69B420',c:'#FF69B4',b:'#FF69B440'}, medium:{bg:'#FFB6C120',c:'#FFB6C1',b:'#FFB6C140'}, light:{bg:'#99999920',c:'#999',b:'#99999940'} };
              const wc = wCfg[w] || wCfg.light;
              return <span style={{ padding:'0.2rem 0.55rem', borderRadius:'8px', fontSize:'0.72rem', fontWeight:'600', backgroundColor:wc.bg, color:wc.c, border:`1px solid ${wc.b}` }}>{w}</span>;
            };

            // Apply filters
            const filtered = deptTasks.filter(t => {
              const s = getTaskStatus(t);
              const q = taskSearch.toLowerCase();
              if (q && !t.title?.toLowerCase().includes(q) && !t.assignee_name?.toLowerCase().includes(q) && !t.description?.toLowerCase().includes(q)) return false;
              if (taskFilterStatus !== 'all' && s !== taskFilterStatus) return false;
              if (taskFilterWeight !== 'all' && t.weight !== taskFilterWeight) return false;
              if (taskFilterAssignee && String(t.assignee_id) !== taskFilterAssignee && t.assignee_name !== taskFilterAssignee) return false;
              if (taskFilterDateFrom && t.scheduled_date && t.scheduled_date < taskFilterDateFrom) return false;
              if (taskFilterDateTo && t.scheduled_date && t.scheduled_date > taskFilterDateTo) return false;
              return true;
            });

            const totalPages = Math.max(1, Math.ceil(filtered.length / TASKS_PER_PAGE));
            const safePage = Math.min(taskPage, totalPages);
            const paginated = filtered.slice((safePage - 1) * TASKS_PER_PAGE, safePage * TASKS_PER_PAGE);

            const uniqueAssignees = [...new Map(deptTasks.map(t => [t.assignee_name, t])).values()];

            const inputStyle = { padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#0d0d0d', color: '#fff', fontSize: '0.85rem' };
            const subTabStyle = (active) => ({ padding: '0.5rem 1.2rem', borderRadius: '8px', border: `1px solid ${active ? '#FFB6C1' : '#333'}`, backgroundColor: active ? '#FFB6C120' : 'transparent', color: active ? '#FFB6C1' : '#666', cursor: 'pointer', fontWeight: active ? '700' : '400', fontSize: '0.85rem', transition: 'all 0.2s' });

            return (
              <div className="dashboard-card full-width">
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <h2><FontAwesomeIcon icon={faClipboardList} /> {selectedDept.name} — Tasks</h2>
                  <button style={btnStyle} onClick={() => setShowAssignTaskModal(true)}>
                    <FontAwesomeIcon icon={faPlus} /> Assign Task
                  </button>
                </div>

                {/* Sub-tabs: Assigned Tasks | Reports */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #222', paddingBottom: '0.75rem' }}>
                  <button style={subTabStyle(taskSubView === 'list')} onClick={() => setTaskSubView('list')}>📋 Assigned Tasks</button>
                  <button style={subTabStyle(taskSubView === 'report')} onClick={() => { setTaskSubView('report'); fetchDeptReport(selectedDept); }}>📊 Reports</button>
                </div>

                {/* ========= LIST SUB-VIEW ========= */}
                {taskSubView === 'list' && (
                  <>
                    {/* Filter Bar */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.6rem', marginBottom: '1rem', padding: '1rem', backgroundColor: '#0d0d0d', borderRadius: '10px', border: '1px solid #222' }}>
                      <input placeholder="🔍 Search tasks…" value={taskSearch} onChange={e => { setTaskSearch(e.target.value); setTaskPage(1); }}
                        style={{ ...inputStyle, gridColumn: isMobile ? '1/-1' : 'span 2' }} />
                      <select value={taskFilterStatus} onChange={e => { setTaskFilterStatus(e.target.value); setTaskPage(1); }} style={inputStyle}>
                        <option value="all">All Statuses</option>
                        <option value="ongoing">🟡 Ongoing</option>
                        <option value="late">🔴 Late</option>
                        <option value="completed">✅ Completed</option>
                      </select>
                      <select value={taskFilterWeight} onChange={e => { setTaskFilterWeight(e.target.value); setTaskPage(1); }} style={inputStyle}>
                        <option value="all">All Weights</option>
                        <option value="light">Light</option>
                        <option value="medium">Medium</option>
                        <option value="heavy">Heavy</option>
                      </select>
                      <select value={taskFilterAssignee} onChange={e => { setTaskFilterAssignee(e.target.value); setTaskPage(1); }} style={inputStyle}>
                        <option value="">All Assignees</option>
                        {uniqueAssignees.map(t => <option key={t.assignee_name} value={t.assignee_name}>{t.assignee_name}</option>)}
                      </select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <label style={{ color: '#666', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>From</label>
                        <input type="date" value={taskFilterDateFrom} onChange={e => { setTaskFilterDateFrom(e.target.value); setTaskPage(1); }} style={inputStyle} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <label style={{ color: '#666', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>To</label>
                        <input type="date" value={taskFilterDateTo} onChange={e => { setTaskFilterDateTo(e.target.value); setTaskPage(1); }} style={inputStyle} />
                      </div>
                      <button onClick={() => { setTaskSearch(''); setTaskFilterStatus('all'); setTaskFilterWeight('all'); setTaskFilterDateFrom(''); setTaskFilterDateTo(''); setTaskFilterAssignee(''); setTaskPage(1); }}
                        style={{ ...inputStyle, backgroundColor: '#1a1a1a', border: '1px solid #444', color: '#999', cursor: 'pointer' }}>Clear</button>
                    </div>

                    {/* Summary chips */}
                    <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                      {[{label: `${filtered.length} Matching`, color:'#aaa'}, {label:`${filtered.filter(t=>getTaskStatus(t)==='late').length} Late`, color:'#ef4444'}, {label:`${filtered.filter(t=>getTaskStatus(t)==='ongoing').length} Ongoing`, color:'#f59e0b'}, {label:`${filtered.filter(t=>getTaskStatus(t)==='completed').length} Done`, color:'#4ade80'}].map(({label,color}) => (
                        <span key={label} style={{ fontSize:'0.78rem', padding:'0.2rem 0.65rem', borderRadius:'20px', backgroundColor:`${color}15`, color, border:`1px solid ${color}30` }}>{label}</span>
                      ))}
                    </div>

                    {loadingDeptTasks ? (
                      <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#FFB6C1' }} />
                      </div>
                    ) : paginated.length === 0 ? (
                      <p style={{ color: '#999', textAlign: 'center', padding: '2rem' }}>No tasks match your filters.</p>
                    ) : (
                      <>
                        <div style={{ overflowX: 'auto' }}>
                          <table className="employees-table">
                            <thead>
                              <tr><th>Task</th><th>Assigned To</th><th>Weight</th><th>Due Date</th><th>Status</th></tr>
                            </thead>
                            <tbody>
                              {paginated.map(task => {
                                const isLate = getTaskStatus(task) === 'late';
                                return (
                                  <tr key={task.id} style={{ borderLeft: isLate ? '3px solid #ef4444' : 'none' }}>
                                    <td>
                                      <strong style={{ color: isLate ? '#ef4444' : '#fff' }}>{task.title}</strong>
                                      {task.description && <p style={{ color: '#999', fontSize: '0.8rem', margin: '0.2rem 0 0' }}>{task.description}</p>}
                                    </td>
                                    <td>
                                      <div>{task.assignee_name}</div>
                                      <div style={{ color: '#999', fontSize: '0.8rem' }}>{task.assignee_email}</div>
                                    </td>
                                    <td>{weightBadge(task.weight)}</td>
                                    <td style={{ color: isLate ? '#ef4444' : '#aaa', fontWeight: isLate ? '600' : '400' }}>{task.scheduled_date || '-'}</td>
                                    <td>{statusBadge(task)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
                            <button onClick={() => setTaskPage(p => Math.max(1, p-1))} disabled={safePage === 1}
                              style={{ ...ghostBtnStyle, opacity: safePage === 1 ? 0.4 : 1 }}>‹ Prev</button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                              <button key={p} onClick={() => setTaskPage(p)}
                                style={{ ...ghostBtnStyle, backgroundColor: p === safePage ? '#FFB6C120' : 'transparent', borderColor: p === safePage ? '#FFB6C1' : '#444', color: p === safePage ? '#FFB6C1' : '#888', minWidth: '2rem', justifyContent: 'center' }}>
                                {p}
                              </button>
                            ))}
                            <button onClick={() => setTaskPage(p => Math.min(totalPages, p+1))} disabled={safePage === totalPages}
                              style={{ ...ghostBtnStyle, opacity: safePage === totalPages ? 0.4 : 1 }}>Next ›</button>
                            <span style={{ color: '#666', fontSize: '0.8rem' }}>Page {safePage}/{totalPages} · {filtered.length} tasks</span>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* ========= REPORT SUB-VIEW ========= */}
                {taskSubView === 'report' && (
                  <>
                    {/* Report date range */}
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', padding: '1rem', backgroundColor: '#0d0d0d', borderRadius: '10px', border: '1px solid #222' }}>
                      <label style={{ color: '#999', fontSize: '0.85rem' }}>Report Range:</label>
                      <input type="date" value={reportDateFrom} onChange={e => setReportDateFrom(e.target.value)} style={{ padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#1a1a1a', color: '#fff', fontSize: '0.85rem' }} />
                      <span style={{ color: '#666' }}>to</span>
                      <input type="date" value={reportDateTo} onChange={e => setReportDateTo(e.target.value)} style={{ padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#1a1a1a', color: '#fff', fontSize: '0.85rem' }} />
                      <button onClick={() => fetchDeptReport(selectedDept)} style={btnStyle}>
                        {loadingReport ? <><FontAwesomeIcon icon={faSpinner} spin /> Generating…</> : '📊 Generate Report'}
                      </button>
                    </div>

                    {loadingReport ? (
                      <div style={{ textAlign: 'center', padding: '3rem' }}><FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#FFB6C1' }} /></div>
                    ) : !deptReport ? (
                      <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📊</div>
                        <p>Click "Generate Report" to view task completion insights.</p>
                      </div>
                    ) : (
                      <>
                        {/* KPI cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                          {[{ label: 'Total Tasks', value: deptReport.total ?? deptTasks.length, color: '#FFB6C1' },
                            { label: 'Completed', value: deptReport.completed, color: '#4ade80' },
                            { label: 'Late / Overdue', value: deptReport.late, color: '#ef4444' },
                            { label: 'Completion Rate', value: `${deptReport.completion_rate ?? 0}%`, color: '#f59e0b' },
                          ].map(({ label, value, color }) => (
                            <div key={label} style={{ backgroundColor: '#1a1a1a', border: `1px solid ${color}30`, borderRadius: '12px', padding: '1rem', textAlign: 'center' }}>
                              <div style={{ fontSize: '0.78rem', color: '#999', marginBottom: '0.3rem' }}>{label}</div>
                              <div style={{ fontSize: '1.8rem', fontWeight: '800', color }}>{value}</div>
                            </div>
                          ))}
                        </div>

                        {/* Completion bar */}
                        <div style={{ marginBottom: '1.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                            <span style={{ color: '#aaa', fontSize: '0.85rem' }}>Overall Completion</span>
                            <span style={{ color: '#4ade80', fontWeight: '700' }}>{deptReport.completion_rate ?? 0}%</span>
                          </div>
                          <div style={{ height: '10px', borderRadius: '8px', backgroundColor: '#1a1a1a', border: '1px solid #333', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${deptReport.completion_rate ?? 0}%`, backgroundColor: (deptReport.completion_rate ?? 0) >= 75 ? '#4ade80' : (deptReport.completion_rate ?? 0) >= 40 ? '#f59e0b' : '#ef4444', borderRadius: '8px', transition: 'width 0.6s ease' }} />
                          </div>
                        </div>

                        {/* Per-assignee breakdown */}
                        {deptReport.by_assignee && deptReport.by_assignee.length > 0 && (
                          <div>
                            <h3 style={{ color: '#FFB6C1', marginBottom: '0.75rem', fontSize: '1rem' }}>Per-Member Breakdown</h3>
                            <table className="employees-table">
                              <thead>
                                <tr><th>Member</th><th>Total</th><th>Completed</th><th>Late</th><th>Rate</th></tr>
                              </thead>
                              <tbody>
                                {deptReport.by_assignee.map(row => {
                                  const rate = row.total ? Math.round((row.completed / row.total) * 100) : 0;
                                  return (
                                    <tr key={row.name}>
                                      <td style={{ fontWeight: '600' }}>{row.name}</td>
                                      <td>{row.total}</td>
                                      <td><span style={{ color: '#4ade80', fontWeight: '600' }}>{row.completed}</span></td>
                                      <td><span style={{ color: row.late > 0 ? '#ef4444' : '#666', fontWeight: row.late > 0 ? '700' : '400' }}>{row.late}</span></td>
                                      <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <div style={{ flex: 1, height: '6px', borderRadius: '4px', backgroundColor: '#1a1a1a', border: '1px solid #333', overflow: 'hidden', minWidth: '60px' }}>
                                            <div style={{ height: '100%', width: `${rate}%`, backgroundColor: rate >= 75 ? '#4ade80' : rate >= 40 ? '#f59e0b' : '#ef4444', borderRadius: '4px' }} />
                                          </div>
                                          <span style={{ color: rate >= 75 ? '#4ade80' : rate >= 40 ? '#f59e0b' : '#ef4444', fontSize: '0.8rem', fontWeight: '700', minWidth: '34px' }}>{rate}%</span>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ===== HEATMAP TAB ===== */}
      {activeTab === 'heatmap' && (
        <div className="dashboard-grid">
          <div className="dashboard-card full-width">
            <h2><FontAwesomeIcon icon={faCalendarAlt} /> Team Workload Heatmap (Last 7 Days)</h2>
            <p style={{ marginBottom: '1.5rem', color: '#9ca3af' }}>Visual representation of task load across your team</p>
            {heatmap.length > 0 ? (
              <>
                <div className="heatmap-container">
                  {heatmap.map((day, dayIndex) => (
                    <div key={dayIndex} className="heatmap-day">
                      <div className="heatmap-date">
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      <div className="heatmap-cells">
                        {day.employees.map((emp, empIndex) => (
                          <div key={empIndex} className="heatmap-cell"
                            style={{ backgroundColor: getLoadColor(emp.load_score), opacity: emp.task_count === 0 ? 0.2 : 0.8 }}
                            title={`${emp.email}: ${emp.task_count} tasks (Load: ${emp.load_score})`}>
                            {emp.task_count}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="heatmap-legend" style={{ marginTop: '1.5rem' }}>
                  <h4>Legend:</h4>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    {[{ color: '#10b981', label: 'Light (1-3)' }, { color: '#f59e0b', label: 'Medium (4-6)' }, { color: '#ef4444', label: 'Heavy (7+)' }].map(({ color, label }) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: '20px', height: '20px', backgroundColor: color, borderRadius: '4px' }}></div>
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
                <FontAwesomeIcon icon={faCalendarAlt} size="3x" style={{ marginBottom: '1rem', opacity: 0.3 }} />
                <p>No workload data available yet.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== MODALS ===== */}

      {/* Invite Employee Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}
          style={isMobile ? { alignItems: 'flex-end', padding: 0 } : {}}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faUserPlus} /> Invite Employee</h2>
              <button className="modal-close" onClick={() => setShowInviteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <form onSubmit={handleInviteEmployee}>
              <div className="form-group">
                <label>Employee Email</label>
                <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  placeholder="employee@example.com" required disabled={inviting} />
                <small style={{ color: '#999', fontSize: '0.85rem', marginTop: '0.5rem', display: 'block' }}>
                  The user must already have a BestieConnect account.
                </small>
              </div>
              <div className="form-group">
                <label>Role</label>
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} disabled={inviting}
                  style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#fff', color: '#333', fontSize: '1rem', width: '100%' }}>
                  <option value="employee">Employee</option>
                  <option value="manager">Manager</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowInviteModal(false)} disabled={inviting}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={inviting}>
                  {inviting ? <><FontAwesomeIcon icon={faSpinner} spin /> Inviting...</> : <><FontAwesomeIcon icon={faUserPlus} /> Invite</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Department Modal */}
      {showDeptModal && (
        <div className="modal-overlay" onClick={() => setShowDeptModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faSitemap} /> {editingDept ? 'Edit Department' : 'New Department'}</h2>
              <button className="modal-close" onClick={() => setShowDeptModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <form onSubmit={saveDepartment}>
              <div className="form-group">
                <label>Department Name *</label>
                <input type="text" value={deptForm.name} onChange={e => setDeptForm({ ...deptForm, name: e.target.value })}
                  placeholder="e.g. Engineering, Marketing" required disabled={savingDept} />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={deptForm.description} onChange={e => setDeptForm({ ...deptForm, description: e.target.value })}
                  placeholder="Brief description of this department" rows={3} disabled={savingDept}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#1a1a1a', color: '#fff', resize: 'vertical' }} />
              </div>
              <div className="form-group">
                <label>Assign Manager</label>
                <select value={deptForm.manager_id} onChange={e => setDeptForm({ ...deptForm, manager_id: e.target.value })} disabled={savingDept}
                  style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#fff', color: '#333', fontSize: '1rem', width: '100%' }}>
                  <option value="">— No manager —</option>
                  {managers.map(m => (
                    <option key={m.user_id} value={m.user_id}>{m.full_name} ({m.email})</option>
                  ))}
                </select>
                {managers.length === 0 && (
                  <small style={{ color: '#f59e0b', fontSize: '0.8rem', marginTop: '0.4rem', display: 'block' }}>
                    No managers found. Invite employees with the "Manager" role first.
                  </small>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeptModal(false)} disabled={savingDept}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={savingDept}>
                  {savingDept ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : editingDept ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && selectedDept && (
        <div className="modal-overlay" onClick={() => setShowAddMemberModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faUserPlus} /> Add Member to {selectedDept.name}</h2>
              <button className="modal-close" onClick={() => setShowAddMemberModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <form onSubmit={addMember}>
              <div className="form-group">
                <label>Select Employee</label>
                <select value={selectedMemberUserId} onChange={e => setSelectedMemberUserId(e.target.value)} disabled={addingMember}
                  style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#fff', color: '#333', fontSize: '1rem', width: '100%' }}>
                  <option value="">— Choose employee —</option>
                  {availableToAdd.map(emp => (
                    <option key={emp.user_id} value={emp.user_id}>{emp.full_name} ({emp.email})</option>
                  ))}
                </select>
                {availableToAdd.length === 0 && (
                  <small style={{ color: '#999', fontSize: '0.8rem', marginTop: '0.4rem', display: 'block' }}>
                    All employees are already in this department.
                  </small>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddMemberModal(false)} disabled={addingMember}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={addingMember || availableToAdd.length === 0}>
                  {addingMember ? <><FontAwesomeIcon icon={faSpinner} spin /> Adding...</> : <><FontAwesomeIcon icon={faUserPlus} /> Add Member</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Task Modal */}
      {showAssignTaskModal && selectedDept && (
        <div className="modal-overlay" onClick={() => setShowAssignTaskModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faClipboardList} /> Assign Task — {selectedDept.name}</h2>
              <button className="modal-close" onClick={() => setShowAssignTaskModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <form onSubmit={assignTask}>
              <div className="form-group">
                <label>Task Title *</label>
                <input type="text" value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="What needs to be done?" required disabled={assigningTask} />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={taskForm.description} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Task details (optional)" rows={2} disabled={assigningTask}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#1a1a1a', color: '#fff', resize: 'vertical' }} />
              </div>
              <div className="form-group">
                <label>Assign To *</label>
                <select value={taskForm.assignee_id} onChange={e => setTaskForm({ ...taskForm, assignee_id: e.target.value })} disabled={assigningTask}
                  style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#fff', color: '#333', fontSize: '1rem', width: '100%' }}>
                  <option value="">— Select department member —</option>
                  {deptMembersForTask.map(m => (
                    <option key={m.user_id} value={m.user_id}>{m.user_name} ({m.user_email})</option>
                  ))}
                </select>
                {deptMembersForTask.length === 0 && (
                  <small style={{ color: '#f59e0b', fontSize: '0.8rem', marginTop: '0.4rem', display: 'block' }}>
                    Add members to this department first before assigning tasks.
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>Weight</label>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  {[{ value: 'light', label: 'Light', color: '#999' }, { value: 'medium', label: 'Medium', color: '#FFB6C1' }, { value: 'heavy', label: 'Heavy', color: '#FF69B4' }].map(w => (
                    <button key={w.value} type="button" disabled={assigningTask}
                      onClick={() => setTaskForm({ ...taskForm, weight: w.value })}
                      style={{
                        flex: 1, padding: '0.6rem',
                        backgroundColor: taskForm.weight === w.value ? `${w.color}30` : '#0a0a0a',
                        border: `2px solid ${taskForm.weight === w.value ? w.color : '#333'}`,
                        borderRadius: '8px', color: taskForm.weight === w.value ? w.color : '#999',
                        cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s', fontSize: '0.85rem'
                      }}>
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>Due Date</label>
                <input type="date" value={taskForm.due_date} onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  disabled={assigningTask}
                  style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#1a1a1a', color: '#fff', width: '100%' }} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAssignTaskModal(false)} disabled={assigningTask}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={assigningTask || deptMembersForTask.length === 0}>
                  {assigningTask ? <><FontAwesomeIcon icon={faSpinner} spin /> Assigning...</> : <><FontAwesomeIcon icon={faTasks} /> Assign Task</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDashboard;
